import React, { useState } from 'react';
import { format, isAfter, isBefore, isWithinInterval } from 'date-fns';
import { ConfirmationModal } from './ConfirmationModal';
// Import the base type that allows string | Date
import { Campaign as BaseCampaignType } from '../types/Campaign';
import { sanitizeHtml, encodeHtml } from '../utils/sanitize';

// Internal type used within the component for strict Date objects
interface InternalCampaignType extends Omit<BaseCampaignType, 'startDate' | 'endDate'> {
  startDate: Date;
  endDate: Date;
}

// Campaign status type
type CampaignStatus = 'active' | 'upcoming' | 'completed';

interface CampaignListProps {
  campaigns: InternalCampaignType[]; // Expect the stricter type with Date objects
  onEditCampaign: (campaign: BaseCampaignType) => void; // Still pass Base type up
  onDeleteCampaign: (campaignId: string) => void;
}

export const CampaignList: React.FC<CampaignListProps> = ({ 
  campaigns, // Receives InternalCampaignType[] directly now
  onEditCampaign, 
  onDeleteCampaign 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'promotion' | 'content' | 'rcf_planner'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | CampaignStatus>('all');
  const [campaignToDelete, setCampaignToDelete] = useState<BaseCampaignType | null>(null); // Still use base type here

  // No need to convert internally anymore, props are already the correct type
  // const internalCampaigns: InternalCampaignType[] = campaigns.map(c => ({ ... }));

  // Get the status of a campaign using the prop type
  const getCampaignStatus = (campaign: InternalCampaignType): CampaignStatus => {
    const now = new Date();
    if (isWithinInterval(now, { start: campaign.startDate, end: campaign.endDate })) {
      return 'active';
    } else if (isBefore(now, campaign.startDate)) {
      return 'upcoming';
    } else {
      return 'completed';
    }
  };

  // Filter based on the prop array (which is InternalCampaignType[])
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Handle special case for filtering RCF campaigns
    let matchesType = filterType === 'all';
    if (filterType === 'rcf_planner') {
      matchesType = campaign.type === 'rcf_planner';
    } else if (filterType === 'promotion') {
      // Show RCF campaigns in promotion filter too
      matchesType = campaign.type === 'promotion' || campaign.type === 'rcf_planner';
    } else if (filterType === 'content') {
      // Show RCF campaigns in content filter too
      matchesType = campaign.type === 'content' || campaign.type === 'rcf_planner';
    }
    
    const status = getCampaignStatus(campaign);
    const matchesStatus = filterStatus === 'all' || filterStatus === status;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleDeleteClick = (e: React.MouseEvent, campaign: BaseCampaignType) => { 
    // Need to find the original BaseCampaignType to set for deletion confirmation
    // This logic might need adjustment if IDs aren't guaranteed unique across conversions
    e.stopPropagation();
    setCampaignToDelete(campaign); 
  };

  const handleConfirmDelete = () => {
    if (campaignToDelete) {
      onDeleteCampaign(campaignToDelete.id);
      setCampaignToDelete(null);
    }
  };

  // Get color for status badge
  const getStatusColor = (status: CampaignStatus): string => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-700';
      case 'upcoming':
        return 'bg-purple-100 text-purple-700';
      case 'completed':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  // Get color and label for campaign type badge
  const getTypeInfo = (type: string): { color: string, label: string } => {
    switch (type) {
      case 'promotion':
        return { color: 'bg-green-100 text-green-700', label: 'promotion' };
      case 'content':
        return { color: 'bg-orange-100 text-orange-700', label: 'content' };
      case 'rcf_planner':
        return { color: 'bg-indigo-100 text-indigo-700', label: 'combo' };
      default:
        return { color: 'bg-gray-100 text-gray-700', label: type };
    }
  };

  // Sanitize and format notes for display
  const formatNotes = (notes: string): string => {
    if (!notes) return '';
    
    // First sanitize the HTML to remove any potentially harmful content
    const sanitized = sanitizeHtml(notes);
    
    // For display in the list view, we'll just use plain text
    // Strip all HTML tags and limit to 100 characters
    const plainText = sanitized.replace(/<[^>]*>?/gm, ' ');
    return plainText.substring(0, 100) + (plainText.length > 100 ? '...' : '');
  };

  return (
    <>
      <div className="w-96 bg-white rounded-xl shadow-md">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Scheduled Campaigns</h2>
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
            />
            
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Filter by Type</h3>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-0 ${
                    filterType === 'all'
                      ? 'bg-[#1E3B8C]/10 text-[#1E3B8C]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterType('promotion')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-0 ${
                    filterType === 'promotion'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Promotions
                </button>
                <button
                  onClick={() => setFilterType('content')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-0 ${
                    filterType === 'content'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Content
                </button>
                <button
                  onClick={() => setFilterType('rcf_planner')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-0 ${
                    filterType === 'rcf_planner'
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Combo
                </button>
              </div>
            </div>
            
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2 text-[10px]">Filter by Status</h3>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-0 ${
                    filterStatus === 'all'
                      ? 'bg-[#1E3B8C]/10 text-[#1E3B8C]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterStatus('active')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-0 ${
                    filterStatus === 'active'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setFilterStatus('upcoming')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-0 ${
                    filterStatus === 'upcoming'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Upcoming
                </button>
                <button
                  onClick={() => setFilterStatus('completed')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-0 ${
                    filterStatus === 'completed'
                      ? 'bg-gray-300 text-gray-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Completed
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto max-h-[calc(100vh-440px)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-white [&::-webkit-scrollbar-thumb]:bg-slate-400 hover:[&::-webkit-scrollbar-thumb]:bg-slate-500 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:hidden [&:hover::-webkit-scrollbar]:block">
          <div className="p-4 space-y-3">
            {filteredCampaigns.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No campaigns scheduled</p>
            ) : (
              // Map over the filtered campaigns (InternalCampaignType[])
              filteredCampaigns.map((campaign) => { // campaign here is InternalCampaignType
                  const status = getCampaignStatus(campaign);
                  // Simplification: Assume BaseCampaignType can be passed even if internal is used
                  const originalCampaign = campaign as BaseCampaignType;
                  
                  // Get type info for displaying badge
                  const typeInfo = getTypeInfo(campaign.type);

                  return (
                    <div
                      key={campaign.id}
                      className="p-4 rounded-lg border border-gray-100 hover:border-[#1E3B8C]/20 hover:shadow-sm transition-all cursor-pointer group"
                      onClick={() => onEditCampaign(originalCampaign)} 
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-900 group-hover:text-[#1E3B8C]">{campaign.name}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                          <button
                            onClick={(e) => handleDeleteClick(e, originalCampaign)}
                            className="opacity-0 group-hover:opacity-100 bg-transparent group-hover:bg-gray-100 hover:!bg-transparent transition-opacity p-1 focus:outline-none focus:ring-0 rounded"
                          >
                            <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        <p>Start: {format(campaign.startDate, 'MMM d, yyyy')}</p>
                        <p>End: {format(campaign.endDate, 'MMM d, yyyy')}</p>
                      </div>
                      {campaign.notes && (
                        <div className="mt-2 border-t border-gray-100 pt-2">
                          <div className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            Notes
                          </div>
                          <div 
                            className="text-xs text-gray-600 line-clamp-2 overflow-hidden"
                            dangerouslySetInnerHTML={{ 
                              __html: encodeHtml(formatNotes(campaign.notes)) 
                            }} 
                          />
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(status)}`}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>
        <div className="p-4 border-t border-gray-100 text-center text-sm text-gray-500">
          {filteredCampaigns.length} {filteredCampaigns.length === 1 ? 'campaign' : 'campaigns'} shown
        </div>
      </div>
      
      {/* Deletion confirmation modal */}
        <ConfirmationModal
          isOpen={!!campaignToDelete}
        onClose={() => setCampaignToDelete(null)}
        onConfirm={handleConfirmDelete}
          title="Delete Campaign"
        message={`Are you sure you want to delete "${campaignToDelete?.name}"? This action cannot be undone.`}
        />
    </>
  );
}; 